/**
 * `paused_conversation` event — a customer wrote and the AI will not answer: the conversation
 * is paused, or the channel's AI replies are switched off (`reason`).
 *
 * Nobody is going to answer until a human opens it, and until now nothing anywhere said so.
 * The payload deliberately carries **no message body**: the alert says someone is waiting, not
 * what they said, which keeps the renderers clear of any PII decision.
 */

import type { TenantEventDefinition } from './definition'

export const PAUSED_CONVERSATION_EVENT_TYPE = 'paused_conversation' as const

/** What silenced the AI: a per-conversation pause, or the channel's own AI toggle. */
export type PausedConversationReason = 'conversation_paused' | 'ai_replies_off'

export interface PausedConversationPayload {
  agentId: string
  agentName?: string
  conversationId: string
  /** Channel the customer wrote on, for "where is this waiting" context. */
  channelType: string
  /** Absent on legacy events — renderers treat that as `conversation_paused`. */
  reason?: PausedConversationReason
  /** Display title of the contact, resolved upstream. */
  contactTitle?: string
  /** `metadata.ai_pause.by` — who paused it, or absent on legacy rows. */
  pauseOrigin?: string
  /** Dedup anchor: the alert fires once per conversation session. */
  sessionNumber?: number
  /** CRM lead for this session, when the tenant has one. */
  crmLeadUrl?: string
}

function isPausedConversationPayload(payload: unknown): payload is PausedConversationPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.agentId === 'string' &&
    typeof p.conversationId === 'string' &&
    typeof p.channelType === 'string'
  )
}

export const pausedConversationEventDefinition: TenantEventDefinition<PausedConversationPayload> =
  {
    eventType: PAUSED_CONVERSATION_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isPausedConversationPayload,
    tenant: {
      label: 'Customer waiting in a paused conversation',
      description: "A customer wrote into a conversation the AI will not answer: it is paused, or the channel's AI replies are off.",
      recipients: 'agent_settings',
      mandatory: false,
    },
  }
